import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';
import { getIconBadgeTokens } from 'tio-reimagined/color-tokens';
import type { ReimaginedColor } from 'tio-reimagined/color-tokens';

// ---------------------------------------------------------------------------
// IconBadge – colored icon badge for lists, cards, and section headers
// ---------------------------------------------------------------------------

/**
 * Color theme options for the icon badge
 */
export type IconBadgeColor = ReimaginedColor;

/**
 * Size options for the icon badge
 */
export type IconBadgeSize = 'sm' | 'md' | 'lg';

interface IconBadgeSignature {
  Args: {
    /** Phosphor icon component to display */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Color theme for background and icon */
    color?: IconBadgeColor;
    /** Size variant */
    size?: IconBadgeSize;
  };
  Element: HTMLDivElement;
}

const sizeClasses: Record<IconBadgeSize, { container: string; iconSize: number }> = {
  sm: { container: 'p-1.5 rounded-md', iconSize: 16 },
  md: { container: 'p-2 rounded-lg', iconSize: 20 },
  lg: { container: 'p-3 rounded-xl', iconSize: 24 },
};

const getColorClasses = (color?: IconBadgeColor) => getIconBadgeTokens(color);
const getSizeClasses = (size: IconBadgeSize = 'md') => sizeClasses[size];

/**
 * IconBadge - A colored icon badge for lists and cards
 *
 * Small presentational component for displaying a Phosphor icon with a
 * colored background. Used throughout as a building block for navigation
 * cards, section panels, and list items.
 *
 * @example Basic usage
 * ```gts
 * import IconBadge from 'tio-reimagined/components/icon-badge';
 * import { PhTrophy } from 'ember-phosphor-icons';
 *
 * <IconBadge @icon={{PhTrophy}} @color="violet" />
 * ```
 *
 * @example Large size
 * ```gts
 * <IconBadge @icon={{PhGraduationCap}} @color="emerald" @size="lg" />
 * ```
 */
const IconBadge: TOC<IconBadgeSignature> = <template>
  {{#let (getColorClasses @color) (getSizeClasses @size) as |colors sizes|}}
    <div
      data-test-icon-badge
      class="{{sizes.container}} {{colors.bg}} {{colors.text}} flex-shrink-0"
      aria-hidden="true"
      ...attributes
    >
      {{#let @icon as |Icon|}}
        <Icon @size={{sizes.iconSize}} />
      {{/let}}
    </div>
  {{/let}}
</template>;

export default IconBadge;
export type { IconBadgeSignature };
